import { Injectable } from '@angular/core';
import { StatusBar, Style } from '@capacitor/status-bar';
import { Keyboard } from '@capacitor/keyboard';
import { Capacitor, SystemBars, SystemBarsStyle } from '@capacitor/core';

@Injectable({ providedIn: 'root' })
export class SafeAreaService {
  private inited = false;

  async init(): Promise<void> {
    if (this.inited) {
      return;
    }
    this.inited = true;

    if (Capacitor.getPlatform() === 'web') {
      return;
    }

    try {
      const bgColor =
        getComputedStyle(document.documentElement).getPropertyValue('--ion-background-color').trim() ||
        '#121212';

      const rgb = this.hexToRgb(bgColor);
      const brightness = (rgb.r * 299 + rgb.g * 587 + rgb.b * 114) / 1000;
      const darkBackground = brightness <= 150;

      if (Capacitor.getPlatform() === 'android') {
        // Android 16 (API 36) enforces edge-to-edge. StatusBar overlaysWebView / backgroundColor
        // no longer apply. Capacitor SystemBars injects --safe-area-inset-* CSS variables.
        await SystemBars.setStyle({
          style: darkBackground ? SystemBarsStyle.Dark : SystemBarsStyle.Light,
        });
      } else {
        await StatusBar.setOverlaysWebView({ overlay: false });
        await StatusBar.setBackgroundColor({ color: bgColor });
        await StatusBar.setStyle({ style: darkBackground ? Style.Dark : Style.Light });

        const topPx = this.getStatusBarHeight();
        document.documentElement.style.setProperty('--safe-area-inset-top', `${topPx}px`);
        document.documentElement.style.setProperty('--ion-safe-area-top', `${topPx}px`);
      }
    } catch (err) {
      console.warn('SafeAreaService init error', err);
    }

    // VitalScore: keyboard inset + CSS padding on iOS only. Android uses adjustResize alone.
    if (Capacitor.getPlatform() !== 'ios') {
      return;
    }

    try {
      Keyboard.addListener('keyboardWillShow', (info) => {
        document.documentElement.style.setProperty('--keyboard-inset', `${info.keyboardHeight}px`);
      });
      Keyboard.addListener('keyboardWillHide', () => {
        document.documentElement.style.setProperty('--keyboard-inset', '0px');
      });
      Keyboard.addListener('keyboardDidShow', (info) => {
        document.documentElement.style.setProperty('--keyboard-inset', `${info.keyboardHeight || 0}px`);
      });
      Keyboard.addListener('keyboardDidHide', () => {
        document.documentElement.style.setProperty('--keyboard-inset', '0px');
      });
    } catch {
      // Best-effort on platforms without keyboard plugin support
    }
  }

  private hexToRgb(hex: string): { r: number; g: number; b: number } {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    return result
      ? {
          r: parseInt(result[1], 16),
          g: parseInt(result[2], 16),
          b: parseInt(result[3], 16),
        }
      : { r: 0, g: 0, b: 0 };
  }

  private getStatusBarHeight(): number {
    if (/iphone|ipod|ipad/i.test(navigator.userAgent)) {
      return window.screen.height >= 812 ? 44 : 20;
    }
    return 24;
  }
}
